<template>
  <div v-if="!loading">
    <form v-if="!offeredQuote" @submit.prevent="submit">
      <legend class="sr-only">
        {{ i18n.t('/QuoteActions/Forms/SendOfferTitle') }}
      </legend>
      <div>
        <app-fieldset :title="i18n.t('/QuoteActions/Forms/SendOfferTitle')">
          <form-text-area
            id="emailBody"
            v-model="emailBody"
            name="emailBody"
            title="Email body"
            rows="5"
            :show-label="false"
          />

          <form-checkbox id="sendEmailToCustomer" :title="mailCustomerLabel">
            <input
              id="sendEmailToCustomer"
              v-model="sendEmailToCustomer"
              type="checkbox"
              name="sendEmailToCustomer"
            />
          </form-checkbox>

          <form-checkbox id="sendCopyToEmployee" :title="mailCopyToEmployee">
            <input
              id="sendCopyToEmployee"
              v-model="sendCopyToEmployee"
              type="checkbox"
              name="sendCopyToEmployee"
            />
          </form-checkbox>

          <form-checkbox
            v-if="isStockCarOrder && !additionalQuote"
            id="reserveInIDS"
            :title="i18n.t('/QuoteActions/Forms/ReserveCarInIdsLabel')"
          >
            <input id="reserveInIDS" v-model="reserveInIDS" type="checkbox" name="reserveInIDS" />
          </form-checkbox>

          <form-checkbox
            v-if="reserveOwnStockCarButtonEnabled"
            id="reserveStockCar"
            :title="i18n.t('/QuoteActions/Forms/ReserveOwnStockCarLabel')"
          >
            <input
              id="reserveStockCar"
              v-model="reserveStockCar"
              type="checkbox"
              name="reserveStockCar"
            />
          </form-checkbox>
        </app-fieldset>
      </div>

      <app-button
        :disabled="loading"
        data-qa="send-offer-form-button"
        type="submit"
        button-type="cta"
        class="submit-button"
      >
        {{ i18n.t('/Common/Send') }}
      </app-button>
    </form>
    <div v-else>
      <!-- Offer requested -->
      <div v-if="!hasErrors">
        <!-- success! -->
        <p>
          {{ i18n.t('/QuoteActions/Forms/SendOffer/SuccessOfferHeaderTitle') }}
        </p>

        <p v-if="sendOfferResult.idsStatusMessage">
          {{ sendOfferResult.idsStatusMessage }}
        </p>

        <p v-if="reserveOwnStockCarResponseMessage">
          {{ reserveOwnStockCarResponseMessage }}
        </p>
      </div>

      <div v-else>
        <!-- errors :( -->
        <p>{{ i18n.t('/QuoteActions/Forms/SendOffer/ErrorText') }}</p>
        <pre v-for="(error, index) in sendOfferResult.quoteErrors" :key="index">
        {{ error }}
      </pre>
        <p>{{ i18n.t('/QuoteActions/Forms/SendOffer/RequestMessageLabel') }}</p>
        <pre v-for="(error, index) in sendOfferResult.requestingQuoteErrors" :key="index">
        {{ error }}
      </pre>

        <p v-if="sentToAutolineFailed">
          {{ i18n.t('/QuoteActions/Forms/SendOffer/SendToAutolineErrorMessage') }}
        </p>
      </div>

      <app-button data-qa="close-offer-form-button" @click="$emit('close')">
        {{ i18n.t('/Common/Close') }}
      </app-button>
    </div>
  </div>
  <div v-else>
    <loader />
  </div>
</template>

<script>
import AppFieldset from '@/components/generic/presentational/atoms/appFieldset/AppFieldset.vue'
import AppButton from '@/components/generic/presentational/atoms/appButton/AppButton.vue'
import FormTextArea from '@/components/generic/presentational/atoms/formTextarea/FormTextarea.vue'
import FormCheckbox from '@/components/generic/presentational/atoms/formCheckbox/FormCheckbox.vue'
import quoteOrderService from '@/services/quoteOrderService.js'
import stockCarsService from '@/services/stockCarsService'
import Loader from '@/components/generic/presentational/atoms/loader/Loader.vue'
import EmailBodyHelper from '@/helpers/EmailBodyHelper'
import AdditionalOrder from '@/mixins/additionalOrder.js'
import { mapState, mapGetters } from 'vuex'
import moment from 'moment'

export default {
  components: {
    AppButton,
    AppFieldset,
    FormTextArea,
    FormCheckbox,
    Loader,
  },
  mixins: [AdditionalOrder],
  emits: ['close'],
  data() {
    return {
      i18n: window.VUE_CLI_EXPORTS.plugins.i18n,

      emailBody: '',
      sendEmailToCustomer: false,
      sendCopyToEmployee: true,
      reserveInIDS: false,
      reserveStockCar: false,

      offeredQuote: false,
      loading: false,

      hasErrors: false,

      sendOfferResult: {},
      reserveOwnStockCarResult: {},
    }
  },
  computed: {
    ...mapState({
      isStatusQuote(state, getters) {
        return getters[this.quoteNamespace + '/quoteStatusIsQuote']
      },
      isStatusSalesOrder(state, getters) {
        return getters[this.quoteNamespace + '/quoteStatusIsSalesOrder']
      },
      currentQuote(state, getters) {
        return getters[this.quoteNamespace + '/get']
      },
      isStockCarOrder(state, getters) {
        return getters[this.quoteNamespace + '/isStockCar']
      },
      quoteCar(state, getters) {
        return getters[this.quoteNamespace + '/car']
      },
    }),
    ...mapState('user', ['dealerId']),
    ...mapGetters({
      dealerSettings: 'user/dealerSettings',
    }),
    quoteId() {
      return this.currentQuote.QuoteId
    },
    customerInfo() {
      return this.currentQuote.QuoteDetails.Customer
    },
    mailCustomerLabel() {
      if (this.customerInfo?.CustomerEmail != null)
        return `${this.i18n.t('/QuoteActions/Forms/MailConfirmationToCustomerLabel')}: ${
          this.customerInfo?.CustomerEmail
        }`
      else return this.i18n.t('/QuoteActions/Forms/MailConfirmationToCustomerLabel')
    },
    mailCopyToEmployee() {
      return `${this.i18n.t('/QuoteActions/Forms/MailConfirmationCopyLabel')}: ${
        window.info?.user?.UserInfo?.Email
      }`
    },
    sentToAutolineFailed() {
      return this.isStatusQuote && !this.currentQuote.QuoteSendToAutoline
    },
    stockCarId() {
      return this.quoteCar.StockCarId
    },
    reserveOwnStockCarButtonEnabled() {
      return (
        this.dealerSettings?.claimOwnStockCarEnabled &&
        this.isStockCarOrder &&
        !this.additionalQuote
      )
    },
    reserveOwnStockCarResponseMessage() {
      let responseBody = this.reserveOwnStockCarResult?.Body
      if (!responseBody) return null

      if (!responseBody?.message) {
        const reservationEndDate = moment(responseBody).format('D-M-YYYY')
        const reservationSuccess = this.i18n.t('/QuoteActions/Forms/ReserveOwnStockCarSuccess', {
          date: reservationEndDate,
        })

        return reservationSuccess
      }

      if (responseBody?.dealerId != this.dealerId) return null
      let claimPeriodEndDate = moment.utc(responseBody.claimPeriodEndDate, 'YYYY-MM-DD')
      if (claimPeriodEndDate.isBefore(moment.utc(), 'day')) return null

      let messageTemplate = this.i18n.t('/QuoteActions/Forms/ReserveOwnStockCarAlreadyReserved', {
        date: claimPeriodEndDate.format('DD-MM-YYYY'),
        establishment: responseBody.establishmentId,
        user: responseBody.salesEmail,
      })

      return messageTemplate
    },
  },
  created() {
    // set default emailbody if there isn't one yet
    if (this.currentQuote.QuoteDetails.EmailBodySalesOrder == null)
      this.emailBody = EmailBodyHelper.getRequestSalesorderEmailBody(
        this.currentQuote.QuoteDetails.Customer
      )
    else this.emailBody = this.currentQuote.QuoteDetails.EmailBodySalesOrder
  },
  methods: {
    async submit() {
      this.loading = true
      this.reserveOwnStockCarResult = {}
      this.sendOfferResult = {}

      // first updateQuote, then send offer
      await this.$store.dispatch(`${this.quoteNamespace}/setEmailBodyQuote`, this.emailBody)
      await this.$store.dispatch(`${this.quoteNamespace}/update`)

      if (!this.isStatusSalesOrder) {
        this.sendOfferResult = await quoteOrderService.sendOffer(
          this.quoteId,
          this.sendEmailToCustomer,
          this.sendCopyToEmployee,
          this.reserveInIDS
        )
      } else {
        //triggered for reservation purpose of SalesOrder + not sent to Autoline yet
        let salesOrderParams = {
          quoteId: this.quoteId,
          sendEmailToCustomer: this.sendEmailToCustomer,
          sendCopyToEmployee: this.sendCopyToEmployee,
          digitalSigningParams: null,
          sendSigning: true, //used in BE as reversed sentToAutoline property
          reserveInIds: this.reserveInIDS,
          isApprovalRequest: false,
        }

        // first updateQuote, then request Salesorder
        this.sendOfferResult = await quoteOrderService.requestSalesOrder(salesOrderParams)

        this.sendOfferResult.requestQuoteHasErrors = this.sendOfferResult.salesOrderHasErrors
        this.sendOfferResult.requestingQuoteErrors = this.sendOfferResult.requestingSalesOrderErrors
      }

      this.hasErrors =
        this.sendOfferResult.requestQuoteHasErrors || this.sendOfferResult.quoteHasErrors

      if (this.stockCarId && this.reserveStockCar && !this.hasErrors) {
        let params = {
          stockCarId: this.stockCarId,
          quoteId: this.quoteId,
          establishmentId: this.currentQuote.QuoteDetails?.Dealer?.EstablishmentID,
        }

        this.reserveOwnStockCarResult = await stockCarsService.setStockCarReservation(params)
      }

      this.loading = false
      this.offeredQuote = true
    },
  },
}
</script>

<style lang="scss" scoped>
pre {
  white-space: normal;
}
</style>
